import {
  MAIN_PORTFOLIO_COMPOSITION_CHART_KEYS,
  PARENT_PORTFOLIO_KEYS,
} from "../config/portfolioConfig";
import {
  fetchMainPortfolioAllocation,
  resolveCompositionSeriesName,
  selectActivePortfolioApiId,
} from "../redux/slices/portfolioSlice";
import { contentTypeEnum } from "../util/enumsSharma";
import { similarity } from "../util/variousMethods";
import { buildFailedMainAllocationPie } from "./portfolioAllocationAdapter";
import {
  formatMainAllocationReadyVoice,
} from "./portfolioAllocationMessages";
import { resolveActiveLocale } from "./sharmaReportMessages";

export function matchMainCompositionChart(graphName, threshold = 0.6) {
  const text = graphName != null ? String(graphName) : "";
  let best = { key: null, score: 0 };
  for (const key of MAIN_PORTFOLIO_COMPOSITION_CHART_KEYS) {
    const score = similarity(text, key);
    if (score > best.score) {
      best = { key, score };
    }
  }
  return { ...best, matched: best.score >= threshold };
}

// The pie chart's own data never reaches the realtime agent's context unless a
// caller attaches it to a tool response - without this, the agent can see the
// chart on screen but has no values to answer questions about it, and pushes
// the user to load the table instead.
export function summarizeMainAllocationForVoice(payload) {
  if (!payload?.legendItems?.length) {
    return { positions: [] };
  }
  return {
    seriesName: payload.seriesName,
    positions: payload.legendItems.map(({ name, percentLabel, aum }) => ({
      name,
      percent: percentLabel,
      aum,
    })),
  };
}

export function mainCompositionNotAvailableMessage(locale) {
  const loc = locale ?? resolveActiveLocale();
  if (loc === "es") {
    return "La composición del portafolio principal solo está disponible cuando el portafolio main está seleccionado.";
  }
  return "Main portfolio composition is only available when the main portfolio is selected.";
}

export async function openMainPortfolioCompositionChart({
  dispatch,
  getState,
  setCurrentContentType,
  setOutputText,
  refetch = false,
}) {
  const locale = resolveActiveLocale();
  const state = getState();
  const parentKey = state?.portfolio?.selectedParentKey;
  if (parentKey !== PARENT_PORTFOLIO_KEYS.MAIN) {
    return {
      ok: false,
      confirmationMessage: mainCompositionNotAvailableMessage(locale),
    };
  }

  const pie = state?.portfolio?.mainAllocationPie;
  const status = state?.portfolio?.mainAllocationStatus;
  const activeApiId = selectActivePortfolioApiId(state);
  const cachedApiId = state?.portfolio?.mainAllocationPortfolioApiId;
  const useCache =
    !refetch &&
    status === "succeeded" &&
    pie?.kind === "main_allocation_pie" &&
    activeApiId != null &&
    cachedApiId === activeApiId;

  let payload;
  if (useCache) {
    payload = pie;
  } else {
    try {
      payload = await dispatch(fetchMainPortfolioAllocation()).unwrap();
    } catch (err) {
      payload = buildFailedMainAllocationPie(
        err,
        locale,
        resolveCompositionSeriesName(state)
      );
    }
  }

  setCurrentContentType(contentTypeEnum.graph);
  setOutputText(payload);

  let confirmationMessage = formatMainAllocationReadyVoice(locale);
  if (payload.partialNotice) {
    confirmationMessage = payload.partialNotice;
  } else if (payload.data?.length > 0 && !payload.partial) {
    confirmationMessage = formatMainAllocationReadyVoice(locale);
  }

  return {
    ok: true,
    payload,
    confirmationMessage,
  };
}
